import { Transition } from '@headlessui/react'
import clsx from 'clsx'
import { TargetedEvent } from 'preact/compat'

import { EmailPayload, MessageType } from '../machines/emailReport.machine'
import { Blades } from './icons/Blades'
import { EnvelopeIcon } from './icons/Envelope'
import { TickIcon } from './icons/Tick'

interface EmailInputProps {
  className?: string
  enabled?: boolean
  email?: string
  successful?: boolean
  sending?: boolean
  onChange: (value: string) => void
  onFocus: () => void
  onBlur: () => void
  onSubmit: () => void
  message?: { type: MessageType; text: string }
}

export type EmailStatusResponse = Promise<{ status: string; success: boolean }>
export type EmailCallback = (payload: EmailPayload) => EmailStatusResponse

export function EmailInput({
  className,
  enabled,
  email,
  successful,
  onChange,
  onFocus,
  onBlur,
  onSubmit,
  sending,
}: EmailInputProps) {
  const canSend = Boolean(enabled && !sending)

  const handleChange = (e: TargetedEvent<HTMLInputElement>) => {
    onChange && onChange(e.currentTarget.value)
  }
  const handleSubmit = (e: TargetedEvent<HTMLFormElement>) => {
    e.preventDefault()
    onSubmit && onSubmit()
  }
  return (
    <div className={clsx('flex flex-col', className)}>
      <form className="relative block" onSubmit={handleSubmit}>
        <div className="flex justify-between w-full space-x-px transition-shadow rounded h-14 focus-within:ring focus-within:ring-white/50">
          <input
            type="email"
            name="email"
            id="email"
            value={email}
            onInput={handleChange}
            onFocus={onFocus}
            onBlur={onBlur}
            placeholder="Email me the results"
            className={clsx(
              'w-full h-14 px-3 text-base rounded-tl rounded-bl rounded-r-none border-none text-navy-dark focus:outline-none',
              canSend ? 'placeholder:text-navy-dark' : 'placeholder:text-navy-dark/50',
            )}
          />
          <button
            className={clsx(
              'relative flex items-center justify-center transition-colors duration-200 border rounded-tr rounded-br focus:outline-none shrink-0 w-14 placeholder:transition-colors group',
              !canSend && 'cursor-not-allowed',
              (successful || sending) && 'cursor-default',
              successful ? 'bg-mint-dark border-mint-dark' : 'bg-yellow border-yellow',
              successful
                ? 'text-white'
                : canSend
                ? 'text-black/100  hover:bg-black-80 hover:text-yellow'
                : 'text-black/40',
            )}>
            <Transition
              show={sending}
              className="absolute flex items-center justify-center w-full h-full pointer-events-none"
              enter="transition duration-300 ease-out"
              enterFrom="opacity-0 scale-0"
              enterTo="opacity-100 scale-100"
              leave="transition duration-300 ease-out"
              leaveFrom="opacity-100 scale-100"
              leaveTo="opacity-0 scale-0">
              <Blades className="w-full h-full p-1 stroke-navy/80  animate-spin-med stroke-3 group-focus:scale-105" />
            </Transition>
            <Transition
              show={!sending && successful}
              className="absolute flex items-center justify-center w-full h-full pointer-events-none"
              enter="transition duration-300 ease-out"
              enterFrom="opacity-0 scale-0"
              enterTo="opacity-100 scale-100"
              leave="transition duration-300 ease-out"
              leaveFrom="opacity-100 scale-100"
              leaveTo="opacity-0 scale-0">
              <TickIcon className="w-full h-6" />
            </Transition>
            <Transition
              show={!sending && !successful}
              className="absolute flex items-center justify-center w-full h-full pointer-events-none"
              enter="transition duration-300 ease-out"
              enterFrom="opacity-0 scale-0"
              enterTo="opacity-100 scale-100"
              leave="transition duration-300 ease-out"
              leaveFrom="opacity-100 scale-100"
              leaveTo="opacity-0 scale-0">
              <EnvelopeIcon className={clsx('w-full h-6 stroke-2 group-focus:scale-105')} />
            </Transition>
          </button>
        </div>
      </form>
    </div>
  )
}
